import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useStoreSubdomain, { useStorePath } from '@/hooks/useStoreSubdomain';
import useStoreBranding from '@/hooks/useStoreBranding';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import StoreBrandMark from '@/components/StoreBrandMark';
import PasswordInput from '@/components/PasswordInput';
import { Loader2, ArrowLeft } from 'lucide-react';
import { DEFAULT_STORE_PRIMARY, DEFAULT_STORE_PRIMARY_HOVER } from '@/lib/storeTheme';

export default function StoreLogin() {
  const subdomain = useStoreSubdomain();
  const storePath = useStorePath();
  const { theme, loading: brandingLoading } = useStoreBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const primary = theme?.primary || DEFAULT_STORE_PRIMARY;
  const primaryHover = theme?.primaryHover || DEFAULT_STORE_PRIMARY_HOVER;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'customer') {
        navigate('/my');
      } else {
        setError('Akun ini bukan akun pelanggan untuk toko ini');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login gagal. Periksa email dan password Anda.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center pb-2">
          <Link to={storePath('/')} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4 justify-center">
            <ArrowLeft size={16} className="mr-1" /> Kembali ke Toko
          </Link>
          <StoreBrandMark theme={theme} loading={brandingLoading} />
          <CardTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>Login Pelanggan</CardTitle>
          <CardDescription>Masuk untuk melihat pesanan, kursus, dan membership Anda</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@contoh.com"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Masukkan password"
                required
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              className="w-full text-white"
              style={{ backgroundColor: primary }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = primaryHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = primary; }}
              disabled={loading}
            >
              {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
              Masuk
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-500">
            Belum punya akun?{' '}
            <Link to={storePath('/register')} className="font-medium hover:underline" style={{ color: primary }}>
              Daftar sekarang
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
